import React, { useState } from 'react';
import pluralize from 'pluralize';
import { toast } from 'sonner';
import { z } from 'zod';

import { APIinviteTimelineContributor } from '../../api/Timeline';

import UserAvatar from '../UserAvatar';

import { useStore } from '../../store';

import type { Contributor } from '../../types/contributor';
import type { Timeline } from '../../types/timeline';

const TimelineContributors: React.FC<{ timeline: Timeline }> = ({ timeline }) => {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const contributors = [timeline.user as unknown as Contributor].concat(timeline.contributors);
  const profile = useStore((state) => state.profile);

  const schema = z.object({
    email: z.email(),
  });

  const sendInvite = async () => {
    setError(null);
    const result = schema.safeParse({ email });
    if (!result.success) {
      setError(result.error.issues[0].message);
      return;
    }

    setSending(true);
    try {
      await APIinviteTimelineContributor(timeline.id, email);
      setEmail('');
      toast.success('Your invite has been sent!');
    } catch (err: any) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="timeline-contributors wa-stack wa-gap-s" style={{ '--min-column-size': '16ch' } as React.CSSProperties}>
      <div className="page-eyebrow with-separator">
        Contributors
      </div>
      <div className="timeline-contributors-list wa-stack wa-gap-2xs">
        {contributors.map((contributor) => (
          <div key={contributor.id} className="timeline-contributors-list-item wa-cluster wa-gap-2xs">
            <div className="timeline-contributors-list-item-name">
              <UserAvatar profile={contributor} />
              {contributor.name || contributor.email}
            </div>
            <div className="timeline-contributors-list-item-memories-count">
              {contributor.id === timeline.user.id ? (
                <wa-tag size="xs" className="timeline-memory-card-owner">
                  Owner
                </wa-tag>
              ) : (
                <div className="wa-cluster wa-gap-2xs">
                  {contributor.status === 'accepted' ? (
                    pluralize('memory', (contributor as { memories_count?: number }).memories_count ?? 0, true)
                  ) : (
                    'invited'
                  )}
                  {contributor.user_id === profile?.id && (
                    <wa-tag size="xs" className="timeline-memory-card-you">
                      You
                    </wa-tag>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {timeline.capabilities?.can_update && (
        <div className="wa-stack wa-gap-0">
          <div className="timeline-contributors-invite wa-cluster wa-gap-2xs">
            <wa-input
              className="timeline-contributors-list-item-add"
              name="contributor-email"
              type="email"
              size="xs"
              placeholder="Invite by email"
              disabled={sending}
              onKeyDown={(e) => {
                if (e.key === 'Enter') sendInvite();
              }}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
            value={email}
            />
            <wa-button
              size="xs"
              disabled={sending || !email}
              onClick={sendInvite}
            >
              Invite
            </wa-button>
          </div>
          {error ? (
            <div className="error-message">
              {error}
            </div>
          ) : (
            <div className="hint">
              Contributors can add their own memories to the timeline.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TimelineContributors;
